import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Plane, Hotel, Shield, Users, Star, CheckCircle, ArrowRight } from 'lucide-react';
import { HOME } from '../constants/testIds';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 320, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    axios.get(`${API}/cms/testimonials`).then(r => {
      const shuffled = [...r.data].sort(() => 0.5 - Math.random());
      setTestimonials(shuffled.slice(0, 6));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);
    return () => { el.removeEventListener('scroll', checkScroll); window.removeEventListener('resize', checkScroll); };
  }, [testimonials]);

  const features = [
    {
      icon: <Plane className="h-8 w-8 text-primary" />,
      title: 'Penerbangan Terbaik',
      description: 'Bekerja sama dengan maskapai terpercaya untuk kenyamanan perjalanan Anda'
    },
    {
      icon: <Hotel className="h-8 w-8 text-primary" />,
      title: 'Hotel Berkualitas',
      description: 'Hotel dekat Masjidil Haram dan Masjid Nabawi dengan fasilitas terbaik'
    },
    {
      icon: <Shield className="h-8 w-8 text-primary" />,
      title: 'Aman & Terpercaya',
      description: 'Berizin resmi dan berpengalaman melayani jamaah umrah'
    },
    {
      icon: <Users className="h-8 w-8 text-primary" />,
      title: 'Muthowif Berpengalaman',
      description: 'Pembimbing ibadah profesional yang siap membantu Anda'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        data-testid={HOME.heroSection}
        className="relative h-[600px] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/28209449/pexels-photo-28209449.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] mb-6 leading-tight">
              Wujudkan Impian Umrah Anda
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-2xl mx-auto leading-relaxed">
              Platform pemesanan umrah terpercaya dengan berbagai pilihan paket sesuai kebutuhan dan budget Anda
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pilih-umrah">
                <Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 px-8">
                  Mulai Perjalanan Umrah
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/register">
                <Button size="lg" variant="outline" className="rounded-full border-white text-white hover:bg-white/10 px-8">
                  Daftar Sekarang
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] mb-4">Keunggulan Kami</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Kami berkomitmen memberikan pelayanan terbaik untuk perjalanan ibadah Anda
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-border rounded-2xl">
                  <CardHeader>
                    <div className="mb-4">{feature.icon}</div>
                    <CardTitle className="font-['Outfit'] text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="leading-relaxed">{feature.description}</CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] mb-4">Testimoni Jamaah</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Pengalaman nyata dari jamaah yang telah berangkat bersama kami
            </p>
          </div>

          <div className="relative">
            {canScrollLeft && (
              <button onClick={() => scroll(-1)} className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 bg-background hover:bg-background rounded-full p-2 shadow-md border border-border transition" aria-label="Sebelumnya">
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            {canScrollRight && (
              <button onClick={() => scroll(1)} className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 bg-background hover:bg-background rounded-full p-2 shadow-md border border-border transition" aria-label="Berikutnya">
                <ChevronRight className="h-5 w-5" />
              </button>
            )}
            <div ref={scrollRef} className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
              {testimonials.map((testimonial) => (
                <motion.div
                  key={`${testimonial.name}-${testimonial.package_name}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="flex-none w-80 snap-start"
                >
                  <Card className="h-[200px] rounded-2xl border border-border flex flex-col overflow-hidden">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-3">
                        {testimonial.photo_url ? (
                          <img src={testimonial.photo_url} alt={testimonial.name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                            {testimonial.name.charAt(0)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-sm font-semibold truncate">{testimonial.name}</CardTitle>
                          <CardDescription className="text-xs truncate">{testimonial.package_name}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="mb-2 flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`h-4 w-4 ${i < testimonial.rating ? 'fill-accent text-accent' : 'text-gray-300'}`} />
                        ))}
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">{testimonial.comment}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] mb-4">
            Siap Berangkat Umrah?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Daftarkan diri Anda sekarang dan mulai perjalanan spiritual Anda bersama kami
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/packages">
            <Button size="lg" className="rounded-full bg-white text-primary hover:bg-white/90 px-8">
              Pilih Paket Umrah
            </Button>
          </Link>
          <a href="https://wa.me/6285700040403" target="_blank" rel="noopener noreferrer">
            <Button size="lg" variant="outline" className="rounded-full border-white text-white hover:bg-white/10 px-8">
              <MessageCircle className="h-5 w-5 mr-2" />Hubungi Kami
            </Button>
          </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
